/* eslint-disable prettier/prettier */
export const buildAPRPayload = ({
  loanData,
  loanProgramsData,
  rateData,
  rateDataID,
  miAmount,
  instanceId,
}: {
  loanData: any;
  loanProgramsData: any;
  rateData: any;
  rateDataID: number;
  miAmount: number;
  instanceId: string | null;
}): any | null => {
  const { loanInformation = {}, property = {}, borrowers = [] } = loanData;
  const primaryBorrower = borrowers[0] ?? {};
  if (
    !rateData?.loanTemplatePath ||
    !rateData?.loanTemplateType ||
    !rateData?.loanTemplate
  ) {
    return null;
  }

  const { eligibilityDetails: omittedEligibilityDetails, ...rateDataWithoutEligibility } = rateData;
  const programWithMI = {
    ...rateDataWithoutEligibility,
    TemplatePath: rateData.loanTemplatePath,
    TemplateType: rateData.loanTemplateType,
    // PPE reads this at program level to include EstimatedClosingCost as a prepaid finance charge;
    // it must match qualify (ApplyPrepaidFinanceCharges) or the Get MI APR drops below the qualify APR
    UsePrePaidFinanceCharge: rateData.usePrePaidFinanceCharge ?? false,
    rateDetails: (rateData.rateDetails ?? [])
      .filter((rd: any) => rd.rateDataID === rateDataID)
      .map((rd: any) => {
        return {
          ...rd,
          costDetails: {
            ...rd.costDetails,
            proposedMortgageInsuranceAmount: miAmount,
          },
          priceVal: rd.priceVal*-1,
        };
      }),
  };

  const matchedRateDetail = (rateData.rateDetails ?? []).find(
    (rd: any) => rd.rateDataID === rateDataID,
  );

  const lienPosition = loanInformation.lienPosition ?? 1;
  const amount =
    lienPosition === 1
      ? loanInformation.firstMortgageAmount
      : loanInformation.secondMortgageAmount ?? 0;

  const programTypes = [
    {
      ProgramTypeID: rateData.programTypeID,
      ProgramType1: rateData.program,
      ARM: rateData.adjPeriod != null,
      Term: rateData.term,
      Amortization: rateData.termMonths / 12,
      FixedTerm: rateData.termMonths,
    },
  ];

  return {
    AprPayloadType: 1,
    AprProvider: 0,
    instanceId: instanceId ?? '',
    ProgramTypes: programTypes,
    LUsages: [{ LUsageID: loanInformation.loanPurpose }],
    Rates: {
      TemplatePath: rateData.loanTemplatePath,
      TemplateType: rateData.loanTemplateType,
      LoanId: loanProgramsData?.loanId,
      QualificationId: loanProgramsData?.qualificationId,
      LoanPrograms: [programWithMI],
      ProductGroups: loanProgramsData?.productGroups ?? [],
    },
    Data: {
      LoanId: loanProgramsData?.loanId,
      LUsageID: loanInformation.loanPurpose,
      PUsageID: property.use,
      PTypeID: property.type,
      DeliveryTypeId: loanData.deliveryType,
      CommitmentTypeId: loanInformation.commitmentType,
      Amount: amount,
      PropValue: property.value,
      CashOut: loanInformation.cashOut ?? 0,
      DownpaymentAmount: (property.value ?? 0) - (amount ?? 0),
      LoanProgramTypes: programTypes,
      Rate: matchedRateDetail?.rate,
      Price: matchedRateDetail?.price,
      RateDate: matchedRateDetail?.rateDate,
      FRatio: matchedRateDetail?.fRatio,
      BRatio: matchedRateDetail?.bRatio,
      PnI: matchedRateDetail?.pni,
      ProgPpp: rateData?.ppp,
      Options: rateData?.options,
      Margin: matchedRateDetail?.margin,
      LifetimeCap: rateData?.lifetimeCap,
      Holdback: matchedRateDetail?.holdback,
      LienPos: lienPosition,
      RateDataId: rateDataID,
      RepCredit: primaryBorrower?.borrowerFinanancial?.creditScore,
      ZIP: property.zip,
      RateId: rateData?.rateId,
      City: property.city,
      RateDocInstanceId: rateData?.rateDocInstanceId,
      UsdaGuarantee: loanInformation.usdaGuarantee,
      OccupancyRate: property.occupancyRate,
      UndiscountedRate: matchedRateDetail?.undiscountedRate,
      Apor: rateData?.apor,
      DpeCategory: rateData?.dpeCategory,
      DpeThreshold1: rateData?.dpeThreshold1,
      DpeThreshold2: rateData?.dpeThreshold2,
      StartingAdjRate: rateData?.startingAdjRate,
      StartingAdjPrice: rateData?.startingAdjPrice,
      FinancedMIP: loanInformation?.financedAmount,
      NoClosingCost: loanInformation.noClosingCost,
      CommunityAffordableSecond: loanInformation.communityAffordableSecond,
      LenderFeeWaiver: loanInformation.lenderFeeWaiver,
      EffectiveDate: rateData?.relockRequest?.effectiveDate,
      ProgramVersionId: rateData?.programVersionId,
      IndexValue: rateData?.indexValue,
      MSA: property.msa,
      CountyCode: property.countyCode,
      StateCode: property.stateCode,
      LoanType: loanData.loanType,
      otherHELOCLines: loanInformation.otherHELOCLines,
      otherHELOCDraws: loanInformation.otherHELOCDraws,
      LUsage: {
        LUsageID: loanInformation.loanPurpose,
      },
      PType: {
        PTypeID: property.type,
      },
      PUsage: {
        PUsageID: property.use,
      },
      DeliveryType: loanData.deliveryType,
      CommitmentType: loanInformation.commitmentType,
      PriceDetails: [
        {
          LoanId: loanProgramsData?.loanId,
          LienPos: lienPosition,
          Rate: matchedRateDetail?.rate,
          Price: matchedRateDetail?.price,
          Margin: matchedRateDetail?.margin,
          ProgramId: rateData.programID,
          RateDataId: rateDataID,
        },
      ],
      // loan-level value, the same one em-epps sends PPE on qualify, so prepaid finance charges match the qualify APR
      EstimatedClosingCost: loanInformation.estimatedClosingCost,
      FeeAmountPaidInCash: loanInformation.feeAmountPaidInCash,
      FeeAmount: loanInformation.feeAmount,
      FeeAmountFinanced: loanInformation.feeAmountFinanced,
      FhaVaFeeFactor: loanInformation.feeFactor,
      VAFundingFeeExempted: loanInformation.vaFundingFeeExempted,
    },
  };
};
